"use client"

import { LinkBox, LinkOverlay, Text } from "@chakra-ui/react"
import { Box, BoxProps } from "@chakra-ui/react/box"
import { Flex } from "@chakra-ui/react/flex"
import { domAnimation, LazyMotion, m } from "framer-motion"
import parse from "html-react-parser"
import React from "react"
import colors from "simple-color-functions"

import { Skill } from "@/types/database"
import { getSafeHtmlParserOptions } from "@/utils/getSafeHtmlParserOptions"

type MySkillsCardProps = BoxProps & {
  skill: Skill
}

const skillsURL = `${process.env.URL}/work?tags=`

const MySkillsCard: React.FC<MySkillsCardProps> = ({ skill }) => {
  return (
    <LazyMotion features={domAnimation}>
      <Box
        _after={{
          bgColor: colors(skill.icon_color).alpha(0.35).css(),
          borderRadius: "xl",
          boxShadow: `0 0 0 rgb(0 0 0 / 0.122), 0 0 0 0 ${colors(skill.icon_color).alpha(0.95).css()}`,
          content: "''",
          display: "block",
          height: "100%",
          left: "0px",
          position: "absolute",
          top: "0px",
          transition: "transform 0.2s ease-out, box-shadow 0.2s ease-out",
          width: "100%",
          zIndex: "-10",
        }}
        _dark={{
          borderColor: "gray.700",
        }}
        _hover={{
          "&:after": {
            boxShadow: `0 0 4px rgb(0 0 0 / 0.122), -3px -3px 0 0 ${colors(skill.icon_color).alpha(0.95).css()}`,
            transform: "translate(-3px, -3px)",
          },
        }}
        asChild
        borderRadius={"xl"}
        flexGrow={1}
        gap="6px"
        height={"100%"}
        overflow="visible"
        position="relative"
        transform="scale(1)"
        transition="transform 0.2s ease-out"
        zIndex="1"
      >
        <m.div
          transition={{ duration: 0.01, ease: "easeOut" }}
          whileHover={{ scale: 1, x: 6, y: 6 }}
        >
          <LinkBox borderColor={"transparent"} height="100%" width="100%">
            <LinkOverlay
              alignContent="center"
              alignItems="center"
              display={"flex"}
              flexDirection="row"
              flexWrap="wrap"
              height="100%"
              href={`${skillsURL}${skill.slug}`}
              justifyContent="flex-start"
              padding="0"
              rel="noopener"
              title={skill.name + " Skill"}
              width="100%"
            >
              <Box
                _hover={{
                  borderColor: "border.muted",
                }}
                alignContent="center"
                alignItems="center"
                bgColor={"bg.paper"}
                borderColor={"border.emphasized"}
                borderRadius={"xl"}
                borderWidth="1px"
                display="flex"
                flexDirection="column"
                flexWrap="nowrap"
                height="100%"
                justifyContent="center"
                width="100%"
              >
                {null === skill.svg_color_icon ? (
                  ""
                ) : (
                  <Flex
                    alignItems={"flex-end"}
                    flexBasis={"60%"}
                    flexGrow={1}
                    position="relative"
                  >
                    <Flex
                      borderRadius="9999px"
                      color={{
                        base: skill.icon_color,
                        _dark:
                          skill.icon_color === "#000000" ||
                          skill.icon_color === "#000" ||
                          skill.icon_color === "black"
                            ? "#fff"
                            : skill.icon_color,
                      }}
                      css={{
                        "& svg": {
                          height: "42px",
                          width: "42px",
                        },
                      }}
                      padding="0"
                      position="relative"
                    >
                      {parse(skill.svg_color_icon, getSafeHtmlParserOptions())}
                    </Flex>
                  </Flex>
                )}
                <Flex
                  alignItems="center"
                  flexBasis={"40%"}
                  flexGrow="1"
                  flexShrink="0"
                  justifyContent="flex-start"
                  paddingBottom="2"
                  paddingLeft="2"
                  paddingRight="2"
                  paddingTop="1"
                  position="relative"
                >
                  <Text
                    fontFamily="heading"
                    fontSize="small"
                    fontWeight="500"
                    lineHeight="1.2"
                    textAlign="center"
                  >
                    {skill.name}
                  </Text>
                </Flex>
              </Box>
            </LinkOverlay>
          </LinkBox>
        </m.div>
      </Box>
    </LazyMotion>
  )
}

export default MySkillsCard
